import type {FC} from 'react';
import {Fragment} from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type {
  AssistantChartResponse,
  AssistantTableResponse,
} from 'src/api/ProactApiClient';
import {
  CHART_UNAVAILABLE_TEXT,
  chartDataTable,
} from 'src/app/assistant/Chart/chatChartFormat';
import {
  TABLE_EMPTY_TEXT,
  tableDataGrid,
  tableTruncationText,
} from 'src/app/assistant/Table/chatTableFormat';
import {formatUtcDateTime} from 'src/shared/utils/formatters';
import {PRINT_CHART_TOO_TALL_TEXT, planPrintChart} from './printChartPlan';
import type {TranscriptPrintPalette} from './transcriptPrintStyles';
import {TranscriptPrintChart} from './TranscriptPrintChart';
import type {ChatTranscript} from './transcript';
import {TRUNCATED_REPLY_TEXT, transcriptRoleLabel} from './transcript';

const ChartSection: FC<{
  chart: AssistantChartResponse;
  palette: TranscriptPrintPalette;
}> = ({chart, palette}) => {
  const table = chartDataTable(chart);
  const plan = planPrintChart(chart);
  return (
    <section className="chart">
      <div className="chart-figure">
        <p className="chart-title">{chart.title}</p>
        {(plan.kind === 'pie' || plan.kind === 'cartesian') && (
          <TranscriptPrintChart plan={plan} palette={palette} />
        )}
      </div>
      {plan.kind === 'too-tall' && (
        <p className="note">{PRINT_CHART_TOO_TALL_TEXT}</p>
      )}
      {table ? (
        <table>
          <thead>
            <tr>
              {table.header.map((cell, index) => (
                <th key={index} className={index > 0 ? 'num' : undefined}>
                  {cell}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, index) => (
                  <td key={index} className={index > 0 ? 'num' : undefined}>
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="note">{CHART_UNAVAILABLE_TEXT}</p>
      )}
    </section>
  );
};

const TableSection: FC<{table: AssistantTableResponse}> = ({table}) => {
  const grid = tableDataGrid(table);
  return (
    <section className="chart">
      {table.title && <p className="chart-title">{table.title}</p>}
      {grid.rows.length > 0 ? (
        <table>
          <thead>
            <tr>
              {grid.header.map((cell, index) => (
                <th key={index} className={grid.numeric[index] ? 'num' : undefined}>
                  {cell}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grid.rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, index) => (
                  <td key={index} className={grid.numeric[index] ? 'num' : undefined}>
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        !table.truncated && <p className="note">{TABLE_EMPTY_TEXT}</p>
      )}
      {table.truncated && (
        <p className="note">{tableTruncationText(grid.rows.length)}</p>
      )}
    </section>
  );
};

const remarkPlugins = [remarkGfm];

// Same hardening as AssistantMarkdown: no raw HTML, no images, links print as text.
const PrintMarkdown: FC<{text: string}> = ({text}) => (
  <div className="markdown">
    <Markdown
      skipHtml
      remarkPlugins={remarkPlugins}
      disallowedElements={['img']}
      unwrapDisallowed
      components={{a: ({children}) => <>{children}</>}}>
      {text}
    </Markdown>
  </div>
);

export interface TranscriptPrintDocumentProps {
  transcript: ChatTranscript;
  palette: TranscriptPrintPalette;
}

/** Plain-HTML Scout transcript for the isolated print iframe; not for the Mantine tree. */
export const TranscriptPrintDocument: FC<TranscriptPrintDocumentProps> = ({
  transcript,
  palette,
}) => (
  <main>
    <h1>{transcript.title}</h1>
    <ul className="meta">
      <li>Exported {formatUtcDateTime(transcript.exportedAt)}</li>
    </ul>
    {transcript.entries.map(entry => (
      <article key={entry.key} className="message" data-role={entry.role}>
        <p className="message-header">
          {transcriptRoleLabel(entry.role)} · {formatUtcDateTime(entry.createdAt)}
        </p>
        {entry.segments.map((segment, index) => (
          <Fragment key={index}>
            {segment.kind === 'tools' && (
              <ul className="tools">
                {segment.labels.map((label, labelIndex) => (
                  <li key={labelIndex}>{label}</li>
                ))}
              </ul>
            )}
            {segment.kind === 'chart' && (
              <ChartSection chart={segment.chart} palette={palette} />
            )}
            {segment.kind === 'table' && <TableSection table={segment.table} />}
            {segment.kind === 'text' &&
              (entry.role === 'user' ? (
                <p className="user-text">{segment.text}</p>
              ) : (
                <PrintMarkdown text={segment.text} />
              ))}
          </Fragment>
        ))}
        {entry.truncated && <p className="note">{TRUNCATED_REPLY_TEXT}</p>}
      </article>
    ))}
  </main>
);
